import React, { FC, useCallback, useMemo, useState } from 'react';
import { t } from '@lingui/macro';

import { DropdownSmall } from '@rover/icons';
import { Box } from '@rover/kibble/core';
import { MQ } from '@rover/kibble/styles';
import { useTheme } from '@rover/kibble/themes';
import useCountryConfigurationShim from '@rover/shared/js/hooks/useCountryConfigurationShim';
import useFrontendConfigurationShim from '@rover/shared/js/hooks/useFrontendConfigurationShim';

const formattedAvailableLanguages = (
  availableLanguages: string[][]
): Array<{
  key: string;
  label: string;
}> =>
  availableLanguages
    .map(([key, label]) => ({
      key,
      label,
    })) // Sort alphabetically
    .sort((a, b) => (a.label > b.label ? 1 : -1));

const LanguageSelectorMenu: FC = () => {
  const theme = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const { navigationAvailableLanguages, showLanguageSelector } = useCountryConfigurationShim();
  const { language, setLanguage } = useFrontendConfigurationShim();
  const formattedLanguages = useMemo(() => {
    return formattedAvailableLanguages(navigationAvailableLanguages || []);
  }, [navigationAvailableLanguages]);
  const iconWidth = theme.space['4x'];

  const handleMouseEnter = useCallback((): void => {
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback((): void => {
    setIsHovered(false);
  }, []);

  if (!showLanguageSelector) return null;

  return (
    <Box
      position="relative"
      minWidth="auto"
      sx={{
        [MQ.SM_DOWN.toString()]: {
          // Ensure the language selector is first in the navigation order on small screens
          order: -1,
        },
      }}
    >
      <select
        onChange={(e) => setLanguage({ language: e.target.value })}
        value={language}
        aria-label={language}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          textAlign: 'right',
          border: '0 none',
          borderRadius: 0,
          appearance: 'none',
          color: isHovered
            ? theme.textColor.navigation_hover.toString()
            : theme.textColor.navigation.toString(),
          background: 'transparent',
          paddingRight: `calc(${iconWidth.toString()} + ${theme.space['1x'].toString()})`,
        }}
      >
        {formattedLanguages.map(({ key, label }) => (
          <option
            aria-label={language === key ? t`Current language: ${label}` : label}
            lang={key.slice(0, 2)}
            value={key}
            key={key}
            style={{ color: theme.textColor.primary.toString() }}
          >
            {label}
          </option>
        ))}
      </select>

      <DropdownSmall
        style={{
          position: 'absolute',
          right: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          pointerEvents: 'none',
          width: iconWidth.toString(),
          height: iconWidth.toString(),
          fill: theme.textColor.navigation.toString(),
        }}
      />
    </Box>
  );
};

export default LanguageSelectorMenu;
